import React, { useCallback, useEffect, useState } from 'react';
import { Category, SaleParametersData } from '../model/model';
import FormBox from './subcomponents/FormBox';
import TooltipLabel from './subcomponents/TooltipLabel';
import CategoriesList from './subcomponents/CategoriesList';
import FormBoxDropdown from './subcomponents/FormBoxDropdown';
import CustomDropdown from './subcomponents/CustomDropdown';
import { vatValueList } from '../helpers/lists';
import { allegroPaySalesPercentageInfo, allegroSmartSalesPercentageInfo, categoryInfo, customerDeliveryFeeInfo, packagingCostInfo, productValueInfo, shipmentPercentageInfo, vatRateInfo } from '../helpers/tooltips';
import { styled } from 'styled-components';
import { demoState } from '../states/DemoState';
import { useRecoilState, useRecoilValue } from 'recoil';
import DualChoiceButtons from './subcomponents/DualChoiceButtons';
import { newComissionState } from '../states/NewComissionState';
import CustomInput from './subcomponents/CustomInput';

const Title = styled.span`
    font-family: 'Grold', sans-serif;
    font-size: 1rem;
    font-weight: 800;
`;

const CaregoryContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 1rem;
`;

const SaleParametersContainer = styled.div`
    padding: 1rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    ;
`;

const SaleParametersItem = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    align-items: center;
    justify-content: center;
`;

interface SaleParemetersProps {
    saleParameters: SaleParametersData;
    onSaleParametersChange: (saleParameters: SaleParametersData) => void;
}

const SaleParameters: React.FC<SaleParemetersProps> = ({ saleParameters, onSaleParametersChange }) => {
    const [category, setCategory] = useState<Category>(saleParameters.category);
    const [customerDeliveryFee, setCustomerDeliveryFee] = useState<number>(saleParameters.customerDeliveryFee);
    const [packagingCosts, setPackagingCosts] = useState<number>(saleParameters.packagingCosts);
    const [productValue, setProductValue] = useState<number>(saleParameters.productValue);
    const [salesPercentageAllegroPay, setSalesPercentageAllegroPay] = useState<number>(saleParameters.salesPercentageAllegroPay);
    const [salesPercentageSmart, setSalesPercentageSmart] = useState<number>(saleParameters.salesPercentageSmart);
    const [vatValue, setVatValue] = useState<number>(saleParameters.vatValue);
    const [shipmentPercentageBox, setShipmentPercentageBox] = useState<number>(saleParameters.shipmentPercentageBox);
    const [shipmentPercentageCourier, setShipmentPercentageCourier] = useState<number>(saleParameters.shipmentPercentageCourier);
    const [shipmentPercentagePoint, setShipmentPercentagePoint] = useState<number>(saleParameters.shipmentPercentagePoint);
    const [newCommission, setNewCommission] = useRecoilState(newComissionState)
    const demo = useRecoilValue(demoState);

    const handleSaleParametersChange = useCallback(() => {
        onSaleParametersChange({
            category,
            customerDeliveryFee,
            packagingCosts,
            productValue,
            salesPercentageAllegroPay,
            salesPercentageSmart,
            vatValue,
            shipmentPercentageBox,
            shipmentPercentageCourier,
            shipmentPercentagePoint,
        });
    }, [category, customerDeliveryFee, packagingCosts,
        productValue, salesPercentageAllegroPay, salesPercentageSmart, vatValue,
        shipmentPercentageBox, shipmentPercentageCourier, shipmentPercentagePoint]);

    useEffect(() => {
        handleSaleParametersChange();
    }, [category, customerDeliveryFee, packagingCosts,
        productValue, salesPercentageAllegroPay, salesPercentageSmart, vatValue,
        shipmentPercentageBox, shipmentPercentageCourier, shipmentPercentagePoint])

    return (
        <>
            <Title>KROK 2 <br /> PARAMETRY SPRZEDAŻY (NIEZALEŻNE OD FAZ)</Title>
            <FormBox variant='standard' headerVariant='title' headerTitle='Kategoria produktu' width='100%'>
                <CaregoryContainer>
                    <TooltipLabel label="Wpisz nazwę produktu lub jego kod ID" tooltip={categoryInfo} />
                    <CategoriesList
                        category={category}
                        onChange={(category: Category) => {
                            setCategory(category);
                        }}
                    />
                </CaregoryContainer>
            </FormBox>
            <FormBoxDropdown variant="standard" headerContent="DODATKOWE INFORMACJE">
                <SaleParametersContainer>
                    <SaleParametersItem>
                        <TooltipLabel label="Uwzględnij zmiany w prowizjach od 29.02.2024 r."
                            tooltip={
                                <>
                                    Dowiedz się więcej <a href="https://allenarzedzie.pl/przeglad-zmian-w-regulaminie-allegro-luty-marzec-2024-bedzie-drozej/" target="_blank" rel="noopener noreferrer">w naszym poradniku</a>
                                </>
                            } />
                        <DualChoiceButtons
                            option1='Tak'
                            option2='Nie'
                            defaultOption={newCommission ? 'Tak' : 'Nie'}
                            onSelectionChange={(selectedOption) => {
                                setNewCommission(selectedOption === 'Tak');
                                localStorage.setItem("newComissionState", JSON.stringify(selectedOption === 'Tak'));
                            }}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Śr. koszt dostawy non-smart (Brutto)" tooltip={customerDeliveryFeeInfo} />
                        <CustomInput
                            value={customerDeliveryFee}
                            onChange={setCustomerDeliveryFee}
                            type='currency'
                            disabled={demo}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Koszt pakowania (Brutto)" tooltip={packagingCostInfo} />
                        <CustomInput
                            value={packagingCosts}
                            onChange={setPackagingCosts}
                            type='currency'
                            disabled={demo}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Wartość towaru (Brutto)" tooltip={productValueInfo} />
                        <CustomInput
                            value={productValue}
                            onChange={setProductValue}
                            type='currency'
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="% Sprzedaży przez Allegro Pay" tooltip={allegroPaySalesPercentageInfo} />
                        <CustomInput
                            value={salesPercentageAllegroPay}
                            onChange={setSalesPercentageAllegroPay}
                            type='percentage'
                            disabled={demo}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="% Sprzedaży w SMART" tooltip={allegroSmartSalesPercentageInfo} />
                        <CustomInput
                            value={salesPercentageSmart}
                            onChange={setSalesPercentageSmart}
                            type='percentage'
                            disabled={demo}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Stawka VAT" tooltip={vatRateInfo} />
                        <CustomDropdown
                            options={vatValueList}
                            defaultValue={vatValueList.find(vat => vat.vatValue === vatValue)!.value}
                            onChange={(value: number) => {
                                const vat = vatValueList.find(vat => vat.value === value)!.vatValue;
                                setVatValue(vat);
                            }}
                            disabled={demo}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Automaty paczkowe" tooltip={shipmentPercentageInfo} />
                        <CustomInput
                            value={shipmentPercentageBox}
                            type='percentage'
                            onChange={setShipmentPercentageBox}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Kurier" tooltip={shipmentPercentageInfo} />
                        <CustomInput
                            value={shipmentPercentageCourier}
                            type='percentage'
                            onChange={setShipmentPercentageCourier}
                        />
                    </SaleParametersItem>
                    <SaleParametersItem>
                        <TooltipLabel label="Punkty odbioru" tooltip={shipmentPercentageInfo} />
                        <CustomInput
                            value={shipmentPercentagePoint}
                            type='percentage'
                            onChange={setShipmentPercentagePoint}
                        />
                    </SaleParametersItem>
                </SaleParametersContainer>
            </FormBoxDropdown>
        </>
    );
};

export default SaleParameters;